import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from './ui/card';
import { authService } from '../services/authService';
import SEOMeta from './SEOMeta';
import {
  Mail,
  CheckCircle,
  XCircle,
  ArrowLeft,
  RefreshCw,
  Shield,
  Lock,
  UserCheck
} from 'lucide-react';

const MAX_RESENDS = 3;

const VerifyEmail = () => {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [email] = useState(location.state?.email || '');
  const [status, setStatus] = useState('idle'); // 'idle' | 'verifying' | 'success' | 'error' | 'resending'
  const [message, setMessage] = useState('');
  const [resendCount, setResendCount] = useState(0);

  useEffect(() => {
    if (token) {
      setStatus('verifying');
      authService.verifyEmail(token)
        .then(() => {
          setStatus('success');
          setMessage('Your email has been verified. You can now log in.');
        })
        .catch((err) => {
          setStatus('error');
          setMessage(
            err.response?.data?.error || 'This verification link is invalid or has expired.'
          );
        });
    }
  }, [token]);

  const handleResend = async () => {
    if (!email || resendCount >= MAX_RESENDS) return;

    setStatus('resending');
    try {
      await authService.resendVerification(email);
      const newCount = resendCount + 1;
      setResendCount(newCount);
      setStatus('idle');
      setMessage('A new verification email has been sent. Please check your inbox.');
    } catch (err) {
      if (err.response?.data?.code === 'RESEND_LIMIT_EXCEEDED') {
        setResendCount(MAX_RESENDS);
      }
      setStatus('idle');
      setMessage(err.response?.data?.error || 'Failed to resend. Please try again.');
    }
  };

  const renderTokenState = () => {
    if (status === 'verifying') {
      return (
        <div className="flex flex-col items-center space-y-4 py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
          <p className="text-gray-600">Verifying your email...</p>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="flex flex-col items-center space-y-4 py-8">
          <CheckCircle className="h-16 w-16 text-green-500" />
          <h3 className="text-xl font-semibold text-gray-900">Email Verified!</h3>
          <p className="text-gray-600 text-center">{message}</p>
          <Button onClick={() => navigate('/login', { state: { justRegistered: true } })} className="w-full mt-2">
            Go to Login
          </Button>
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className="flex flex-col items-center space-y-4 py-8">
          <XCircle className="h-16 w-16 text-red-500" />
          <h3 className="text-xl font-semibold text-gray-900">Verification Failed</h3>
          <p className="text-gray-600 text-center">{message}</p>
          <RouterLink to="/login" className="text-primary hover:underline text-sm inline-flex items-center mt-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Login
          </RouterLink>
        </div>
      );
    }

    return null;
  };

  const renderPendingState = () => (
    <>
      <CardHeader>
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center">
            <Mail className="h-8 w-8 text-cyan-600" />
          </div>
        </div>
        <CardTitle className="text-2xl text-center">Check Your Email</CardTitle>
        <p className="text-center text-sm text-gray-600 mt-2">
          We sent a verification link to{' '}
          {email ? <strong>{email}</strong> : 'your email address'}.
          Please check your inbox and click the link to activate your account.
        </p>
      </CardHeader>
      <CardContent>
        {message && (
          <div className={`px-4 py-3 rounded text-sm mb-4 border ${
            message.toLowerCase().includes('sent') || message.toLowerCase().includes('new')
              ? 'bg-green-50 text-green-800 border-green-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}>
            {message}
          </div>
        )}
        <div className="space-y-4">
          <p className="text-sm text-gray-500 text-center">
            Didn't receive the email? Check your spam folder, or resend below.
          </p>
          {resendCount < MAX_RESENDS ? (
            <Button
              variant="outline"
              className="w-full"
              onClick={handleResend}
              disabled={status === 'resending' || !email}
            >
              {status === 'resending' ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Resend Verification Email
                  {resendCount > 0 && ` (${MAX_RESENDS - resendCount} remaining)`}
                </>
              )}
            </Button>
          ) : (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded text-sm text-center">
              <p className="font-medium">Maximum resend attempts reached.</p>
              <p className="mt-1">
                Please contact{' '}
                <a href="mailto:support@nivasapro.ai" className="underline hover:text-amber-900">
                  support@nivasapro.ai
                </a>
                {' '}for assistance.
              </p>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter>
        <div className="text-center text-sm w-full">
          <RouterLink to="/login" className="text-primary hover:underline inline-flex items-center justify-center">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Login
          </RouterLink>
        </div>
      </CardFooter>
    </>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEOMeta title="Verify Your Email" description="Verify your NivasaPro account email address." canonical="/verify-email" noIndex />

      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-8 w-8" />
            <span className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              NivasaPro.ai
            </span>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-cyan-600"
          >
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Two Panel Layout */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Left Panel - Security Benefits */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Secure Your Account
              </h2>
              <p className="text-xl text-gray-600">
                Email verification ensures your account is protected and only you have access to your property management platform.
              </p>
            </div>

            <div className="grid gap-6">
              <div className="p-6 rounded-xl border border-gray-200 bg-white hover:border-cyan-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-cyan-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Identity Verified</h3>
                <p className="text-gray-600">
                  Confirming your email ensures only you can access your account, protecting your properties and tenant data from unauthorized access.
                </p>
              </div>

              <div className="p-6 rounded-xl border border-gray-200 bg-white hover:border-cyan-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Lock className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Secure Communication</h3>
                <p className="text-gray-600">
                  Your verified email is the channel for important notifications: maintenance approvals, tenant updates, and account security alerts.
                </p>
              </div>

              <div className="p-6 rounded-xl border border-gray-200 bg-white hover:border-cyan-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <UserCheck className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Trusted Platform</h3>
                <p className="text-gray-600">
                  NivasaPro connects landlords and tenants in a trusted environment. Verified accounts help maintain a high standard of accountability for everyone.
                </p>
              </div>
            </div>
          </div>

          {/* Right Panel - Verify Card */}
          <div className="lg:sticky lg:top-24">
            <Card className="w-full">
              {token ? (
                <CardContent className="pt-6">
                  {renderTokenState()}
                </CardContent>
              ) : (
                renderPendingState()
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
